import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import axiosInstant from "../../api";
import { CustomSelectField } from "../../pages/pro/applicationForm/customSelect";

interface SlackChannelOption {
    label: string;
    value: string;
}

interface Props {
    name: string;
    createFlagName?: string;
    createNameFieldName?: string;
    brand?: { name?: string; rmn?: boolean; slack_channel?: string | null } | null;
    placeholder?: string;
    disabled?: boolean;
    hideCreate?: boolean;
    hideLabel?: boolean;
    onChannelsLoaded?: (channels: SlackChannelOption[]) => void;
    channels?: SlackChannelOption[];
    channelsLoading?: boolean;
}

const expectedNames = (brand?: { name?: string; rmn?: boolean } | null) => {
    if (!brand?.name) return { primary: "", alt: "" };
    const prefix = brand.rmn ? "retailer" : "brand";
    const lower = brand.name.toLowerCase();
    return {
        primary: `${prefix}-${lower.replace(/\s+/g, "-")}`,
        alt: `${prefix}-${lower.replace(/\s+/g, "")}`,
    };
};

export function SlackChannelPicker({
    name,
    createFlagName,
    createNameFieldName,
    brand,
    placeholder,
    disabled,
    hideCreate,
    hideLabel,
    onChannelsLoaded,
    channels: providedChannels,
    channelsLoading,
}: Props) {
    const { values, setFieldValue } = useFormikContext<Record<string, any>>();
    const [fetchedChannels, setFetchedChannels] = useState<SlackChannelOption[]>([]);
    const [fetchedLoading, setFetchedLoading] = useState(false);

    const isProvided = providedChannels !== undefined;
    const channels = isProvided ? providedChannels! : fetchedChannels;
    const loading = isProvided ? !!channelsLoading : fetchedLoading;

    const selectedValue: string = values[name] || "";
    const createFlag: boolean = createFlagName ? !!values[createFlagName] : false;
    const newName: string = createNameFieldName ? values[createNameFieldName] || "" : "";

    const loadChannels = useCallback(async () => {
        setFetchedLoading(true);
        try {
            const res = await axiosInstant.get("/brand-projects/slack/channels");
            const opts: SlackChannelOption[] = (res.data || []).map((c: any) => ({
                label: c.name,
                value: c.id,
            }));
            setFetchedChannels(opts);
            onChannelsLoaded?.(opts);
        } catch {
            setFetchedChannels([]);
            onChannelsLoaded?.([]);
        } finally {
            setFetchedLoading(false);
        }
    }, [onChannelsLoaded]);

    useEffect(() => {
        if (isProvided) return;
        loadChannels();
    }, [isProvided, loadChannels]);

    const expected = useMemo(() => expectedNames(brand), [brand?.name, brand?.rmn]);

    const match = useMemo(() => {
        if (channels.length === 0) return null;
        // A value already selected (e.g. an order's saved channel in edit mode)
        // counts as a match, so we never wrongly offer "create" for an existing
        // selection and the two paths stay mutually exclusive.
        const selectedMatch = selectedValue
            ? channels.find((c) => c.value === selectedValue)
            : null;
        if (selectedMatch) return selectedMatch;
        if (!brand?.name) return null;
        const savedMatch = brand.slack_channel
            ? channels.find((c) => c.value === brand.slack_channel)
            : null;
        if (savedMatch) return savedMatch;
        return (
            channels.find((c) => {
                const l = c.label.toLowerCase();
                return l === expected.primary || l === expected.alt;
            }) || null
        );
    }, [channels, expected, brand?.name, brand?.slack_channel, selectedValue]);

    useEffect(() => {
        if (!brand?.name || channels.length === 0) return;
        if (match) {
            if (!selectedValue) setFieldValue(name, match.value);
            if (createFlag && createFlagName) setFieldValue(createFlagName, false);
        } else if (!hideCreate && !createFlag && !newName && createNameFieldName) {
            // Only suggest a name when the create UI is actually shown; in edit
            // mode (hideCreate) the create field is hidden and must not be mutated.
            setFieldValue(createNameFieldName, expected.primary);
        }
    }, [channels.length, match, brand?.name, expected.primary, selectedValue, createFlag, newName, name, createFlagName, createNameFieldName, hideCreate, setFieldValue]);

    const hasChannels = channels.length > 0;
    const showCreate =
        !hideCreate && !!createFlagName && !!createNameFieldName && !!brand?.name && !match;

    return (
        <>
            {loading && !hasChannels && (
                <Typography sx={{ fontSize: 13, fontFamily: "CircularStd", color: "var(--text-2)", mt: 0.5 }}>
                    Loading Slack channels...
                </Typography>
            )}
            {hasChannels && (
                <CustomSelectField
                    name={name}
                    label="Slack Channel"
                    placeholder={placeholder || "Select Slack Channel"}
                    options={channels}
                    required={false}
                    noLabel={!!hideLabel}
                    disabled={disabled || loading}
                />
            )}
            {hideCreate && !hasChannels && !loading && (
                <Typography sx={{ fontSize: 13, fontFamily: "CircularStd", color: "var(--text-2)", mt: 0.5 }}>
                    No Slack channels available.
                </Typography>
            )}
            {showCreate && (
                <Box sx={{ mt: hasChannels ? 1 : 0 }}>
                    <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                            <Checkbox
                                checked={createFlag}
                                onChange={(e) => {
                                    const v = e.target.checked;
                                    setFieldValue(createFlagName!, v);
                                    if (v) {
                                        setFieldValue(name, "");
                                        if (!newName && expected.primary) {
                                            setFieldValue(createNameFieldName!, expected.primary);
                                        }
                                    }
                                }}
                                sx={{ color: "var(--muted)", "&.Mui-checked": { color: "var(--primary)" } }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 13, fontFamily: "CircularStd" }}>
                                Create Slack Channel
                            </Typography>
                        }
                    />
                    {createFlag && (
                        <Box sx={{ mt: 0.5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Slack channel name"
                                value={newName}
                                onChange={(e) => setFieldValue(createNameFieldName!, e.target.value)}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                        backgroundColor: "var(--surface-2)",
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
}

export default SlackChannelPicker;
